import { GHALISHOR_API_V1_PREFIX } from "./identity-access";
import type { IsoDateTimeString, UuidString } from "./identity-access";

export const MESSAGING_ROUTES = Object.freeze({
  contacts: `${GHALISHOR_API_V1_PREFIX}/messaging/contacts`,
  conversations: `${GHALISHOR_API_V1_PREFIX}/messaging/conversations`,
  conversation: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/messaging/conversations/${encodeURIComponent(id)}`,
  messages: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/messaging/conversations/${encodeURIComponent(id)}/messages`,
  message: (conversationId: string, messageId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/messaging/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`,
  read: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/messaging/conversations/${encodeURIComponent(id)}/read`,
  typing: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/messaging/conversations/${encodeURIComponent(id)}/typing`,
  updates: `${GHALISHOR_API_V1_PREFIX}/messaging/updates`,
  attachment: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/messaging/attachments/${encodeURIComponent(id)}`,
} as const);

export const CONVERSATION_KINDS = Object.freeze([
  "direct",
  "platform_support",
  "business_internal",
  "service_request",
] as const);
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

export const CONVERSATION_CATEGORIES = Object.freeze([
  "general",
  "follow_up",
  "complaint",
  "payment",
  "service_request",
  "operations",
] as const);
export type ConversationCategory = (typeof CONVERSATION_CATEGORIES)[number];

export type MessagingParticipantKind = "platform" | "business" | "customer";

export interface MessagingContact {
  readonly userId: UuidString;
  readonly displayName: string;
  readonly mobileE164: string;
  readonly kind: MessagingParticipantKind;
  readonly roleLabel: string;
  readonly businessId: UuidString | null;
  readonly businessName: string | null;
  readonly serviceRequestId: UuidString | null;
  readonly isOnline: boolean;
}

export interface ListMessagingContactsResponse {
  readonly items: readonly MessagingContact[];
}

export interface MessageAttachmentInput {
  readonly fileName: string;
  readonly mimeType: "image/jpeg" | "image/png" | "image/webp";
  readonly base64: string;
}

export interface MessageAttachmentSummary {
  readonly id: UuidString;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly url: string;
}

export interface ConversationParticipant {
  readonly userId: UuidString;
  readonly displayName: string;
  readonly mobileE164: string;
  readonly kind: MessagingParticipantKind;
  readonly roleLabel: string;
  readonly isOnline: boolean;
  readonly isTyping: boolean;
  readonly lastReadAt: IsoDateTimeString | null;
}

export interface ConversationMessage {
  readonly id: UuidString;
  readonly conversationId: UuidString;
  readonly senderUserId: UuidString;
  readonly senderName: string;
  readonly senderKind: MessagingParticipantKind;
  readonly body: string | null;
  readonly replyToMessageId: UuidString | null;
  readonly attachments: readonly MessageAttachmentSummary[];
  readonly seenByCount: number;
  readonly createdAt: IsoDateTimeString;
  readonly editedAt: IsoDateTimeString | null;
}

export interface ConversationSummary {
  readonly id: UuidString;
  readonly kind: ConversationKind;
  readonly category: ConversationCategory;
  readonly subject: string;
  readonly businessId: UuidString | null;
  readonly businessName: string | null;
  readonly serviceRequestId: UuidString | null;
  readonly status: "open" | "closed";
  readonly unreadCount: number;
  readonly lastMessagePreview: string | null;
  readonly lastMessageAt: IsoDateTimeString | null;
  readonly participants: readonly ConversationParticipant[];
  readonly createdAt: IsoDateTimeString;
}

export interface ConversationDetail extends ConversationSummary {
  readonly messages: readonly ConversationMessage[];
}

export interface ListConversationsResponse {
  readonly items: readonly ConversationSummary[];
  readonly unreadCount: number;
  readonly cursor: IsoDateTimeString;
}

export interface CreateConversationRequest {
  readonly targetUserId?: UuidString | null;
  readonly audience?: "platform_support" | "business_team" | null;
  readonly businessId?: UuidString | null;
  readonly serviceRequestId?: UuidString | null;
  readonly category: ConversationCategory;
  readonly subject: string;
  readonly message: string;
  readonly attachments?: readonly MessageAttachmentInput[];
}

export interface SendConversationMessageRequest {
  readonly body?: string | null;
  readonly replyToMessageId?: UuidString | null;
  readonly attachments?: readonly MessageAttachmentInput[];
}

export interface UpdateConversationMessageRequest {
  readonly body: string;
}

export interface MessagingUpdatesResponse {
  readonly cursor: IsoDateTimeString;
  readonly changedConversationIds: readonly UuidString[];
  readonly unreadCount: number;
}
